import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ReplaySubject, Subject, finalize, takeUntil } from 'rxjs';
import { RapportParcoursFormationService } from 'src/services/rapport-parcours-formation.service';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { EtiquetteService } from 'src/services/etiquette.service';
import {
  EStatutRapportParcours,
  RapportUtilisateurParcoursLigneModel,
  RapportUtilisateurParcoursStatsModel
} from 'src/models/rapport/rapport-parcours-formation.model';
import { CustomPaginatorIntlService } from 'src/services/custom-paginator-intl.service';
import { StringHelpers } from 'src/models/helpers/string-helpers';

@Component({
    selector: 'app-vue-utilisateurs',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatButtonModule,
        MatSelectModule,
        MatMenuModule,
        MatTooltipModule,
        NgxMatSelectSearchModule
    ],
    templateUrl: './vue-utilisateurs.component.html',
    styleUrl: './vue-utilisateurs.component.scss',
    providers: [{ provide: MatPaginatorIntl, useClass: CustomPaginatorIntlService }]
})
export class VueUtilisateursComponent implements OnInit, OnChanges, OnDestroy {
  @Input() filtreParcoursFormationId?: number;
  @Output() voirDetails = new EventEmitter<{ parcoursFormationId: number; utilisateurId: number }>();
  @Output() reinitialiserNavigation = new EventEmitter<void>();

  @ViewChild(MatSort) set matSort(sort: MatSort | undefined) {
    if (!sort) return;
    this.dataSource.sort = sort;
  }

  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator | undefined) {
    if (!paginator) return;
    this.dataSource.paginator = paginator;
  }

  readonly EStatutRapportParcours = EStatutRapportParcours;

  stats?: RapportUtilisateurParcoursStatsModel;
  dataSource = new MatTableDataSource<RapportUtilisateurParcoursLigneModel>([]);
  displayedColumns = ['nomUtilisateur', 'etiquettes', 'nomParcours', 'statut', 'progression', 'dureeVisionnee', 'actions'];

  isLoading = false;
  recherche = '';
  filtreOrganisation: string[] = [];
  filtreParcours: string[] = [];
  filtreEtiquette: string[] = [];
  filtreStatut: EStatutRapportParcours[] = [];
  filtreActifUtilisateur: 'actif' | 'inactif' | 'tous' = 'actif';
  organisations: string[] = [];
  parcours: string[] = [];
  etiquettes: string[] = [];
  statuts = [
    EStatutRapportParcours.NonDemarre,
    EStatutRapportParcours.EnCours,
    EStatutRapportParcours.Complete,
    EStatutRapportParcours.ARefaire
  ];
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];

  organisationFilterCtrl: FormControl = new FormControl();
  parcoursFilterCtrl: FormControl = new FormControl();
  etiquetteFilterCtrl: FormControl = new FormControl();
  statutFilterCtrl: FormControl = new FormControl();

  filteredOrganisations: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  filteredParcours: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  filteredEtiquettes: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  filteredStatuts: ReplaySubject<EStatutRapportParcours[]> = new ReplaySubject<EStatutRapportParcours[]>(1);
  private etiquetteCouleursMap = new Map<string, string>();

  private _onDestroy = new Subject<void>();

  constructor(
    private rapportService: RapportParcoursFormationService,
    private utilisateurService: UtilisateurService,
    private etiquetteService: EtiquetteService,
    private translateService: TranslateService
  ) {}

  obtenirCouleurEtiquette(nomEtiquette: string): string {
    const couleur = this.etiquetteCouleursMap.get(this.normaliserNomEtiquette(nomEtiquette));
    return couleur ?? '#333333';
  }

  obtenirStyleEtiquette(nomEtiquette: string): { [key: string]: string } {
    const backgroundColor = this.obtenirCouleurEtiquette(nomEtiquette);
    return {
      'background-color': backgroundColor,
      color: this.obtenirCouleurTexte(backgroundColor)
    };
  }

  obtenirCouleurTexte(hexColor: string): string {
    if (!hexColor) return '#ffffff';
    const hex = hexColor.replace('#', '');
    if (hex.length < 6) return '#ffffff';
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  }

  private normaliserNomEtiquette(valeur: string | null | undefined): string {
    return (valeur ?? '').trim().toLocaleLowerCase();
  }

  private normaliserCouleurHex(couleur: string | null | undefined): string | null {
    if (!couleur) return null;
    const valeur = couleur.trim();
    if (!valeur) return null;

    const hex = valeur.startsWith('#') ? valeur.slice(1) : valeur;
    const hexValide = /^[0-9a-fA-F]{6}$/.test(hex) || /^[0-9a-fA-F]{3}$/.test(hex);
    return hexValide ? `#${hex}` : valeur;
  }

  private appliquerCouleursEtiquettes(
    etiquettes: { etiquetteId: number; locales: { [key: string]: string }; couleur: string }[]
  ): void {
    this.etiquetteCouleursMap.clear();
    const liste = [...etiquettes].sort((a, b) => a.etiquetteId - b.etiquetteId);

    for (const etiquette of liste) {
      const couleur = this.normaliserCouleurHex(etiquette.couleur);
      if (!couleur) continue;
      for (const nomLocale of Object.values(etiquette.locales ?? {})) {
        const cle = this.normaliserNomEtiquette(nomLocale);
        if (cle && !this.etiquetteCouleursMap.has(cle)) {
          this.etiquetteCouleursMap.set(cle, couleur);
        }
      }
    }
  }

  private chargerCouleursEtiquettes(): void {
    const etiquettesContexte = this.utilisateurService.contexteUtilisateur?.toutesLesEtiquettes;
    if (etiquettesContexte && etiquettesContexte.length > 0) {
      this.appliquerCouleursEtiquettes(etiquettesContexte);
      return;
    }
    this.etiquetteService.obtenirEtiquettes()
      .pipe(takeUntil(this._onDestroy))
      .subscribe(etiquettes => this.appliquerCouleursEtiquettes(etiquettes ?? []));
  }

  ngOnInit(): void {
    this.chargerCouleursEtiquettes();
    this.configurerDataSource();
    this.organisationFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => this.filtrerListe('organisation'));
    this.parcoursFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => this.filtrerListe('parcours'));
    this.etiquetteFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => this.filtrerListe('etiquette'));
    this.statutFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => this.filtrerListe('statut'));
    this.charger();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filtreParcoursFormationId'] && !changes['filtreParcoursFormationId'].firstChange) {
      this.charger();
    }
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  private configurerDataSource(): void {
    this.dataSource.filterPredicate = (row, filter) => {
      const criteres = JSON.parse(filter || '{}') as {
        recherche?: string;
        organisations?: string[];
        parcours?: string[];
        etiquettes?: string[];
        statuts?: EStatutRapportParcours[];
        actifUtilisateur?: 'actif' | 'inactif' | 'tous';
      };
      const recherche = StringHelpers.removeDiacritics(criteres.recherche ?? '');
      const nomUtilisateur = StringHelpers.removeDiacritics(row.nomUtilisateur);
      const nomParcours = StringHelpers.removeDiacritics(row.nomParcours);
      const nomOrganisation = StringHelpers.removeDiacritics(row.nomOrganisation ?? '');
      const etiquettes = row.etiquettes.map(etiquette => StringHelpers.removeDiacritics(etiquette));

      const orgs = criteres.organisations ?? [];
      const pars = criteres.parcours ?? [];
      const etqs = criteres.etiquettes ?? [];
      const stts = criteres.statuts ?? [];
      const actifUtilisateur = criteres.actifUtilisateur ?? 'tous';

      return (!recherche
          || nomUtilisateur.includes(recherche)
          || nomParcours.includes(recherche)
          || nomOrganisation.includes(recherche)
          || etiquettes.some(etiquette => etiquette.includes(recherche)))
        && (orgs.length === 0 || orgs.includes(row.nomOrganisation))
        && (pars.length === 0 || pars.includes(row.nomParcours))
        && (etqs.length === 0 || row.etiquettes.some(e => etqs.includes(e)))
        && (stts.length === 0 || stts.map(Number).includes(Number(row.statut)))
        && (actifUtilisateur === 'tous' || (actifUtilisateur === 'actif') === row.estUtilisateurActif);
    };
  }

  charger(): void {
    this.isLoading = true;
    this.rapportService.obtenirRapportUtilisateurs(this.filtreParcoursFormationId)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(reponse => {
        this.stats = reponse.stats;
        this.dataSource.data = reponse.lignes;
        this.organisations = this.obtenirOptions(reponse.lignes.map(ligne => ligne.nomOrganisation));
        this.parcours = this.obtenirOptions(reponse.lignes.map(ligne => ligne.nomParcours));
        this.etiquettes = this.obtenirOptions(reponse.lignes.flatMap(ligne => ligne.etiquettes));
        this.filteredOrganisations.next(this.organisations.slice());
        this.filteredParcours.next(this.parcours.slice());
        this.filteredEtiquettes.next(this.etiquettes.slice());
        this.filteredStatuts.next(this.statuts.slice());
        this.synchroniserFiltreNavigation(reponse.lignes);
        this.appliquerFiltres();
      });
  }

  private filtrerListe(type: 'organisation' | 'parcours' | 'etiquette' | 'statut'): void {
    if (type === 'organisation') {
      const search = (this.organisationFilterCtrl.value ?? '').toString().toLowerCase();
      this.filteredOrganisations.next(
        !search ? this.organisations.slice() : this.organisations.filter(o => o.toLowerCase().indexOf(search) > -1)
      );
    } else if (type === 'parcours') {
      const search = (this.parcoursFilterCtrl.value ?? '').toString().toLowerCase();
      this.filteredParcours.next(
        !search ? this.parcours.slice() : this.parcours.filter(p => p.toLowerCase().indexOf(search) > -1)
      );
    } else if (type === 'etiquette') {
      const search = (this.etiquetteFilterCtrl.value ?? '').toString().toLowerCase();
      this.filteredEtiquettes.next(
        !search ? this.etiquettes.slice() : this.etiquettes.filter(e => e.toLowerCase().indexOf(search) > -1)
      );
    } else if (type === 'statut') {
      const search = (this.statutFilterCtrl.value ?? '').toString().toLowerCase();
      this.filteredStatuts.next(
        !search ? this.statuts.slice() : this.statuts.filter(s =>
          this.translateService.instant('RAPPORT_PARCOURS.STATUT_' + s).toLowerCase().indexOf(search) > -1
        )
      );
    }
  }

  appliquerRecherche(): void {
    this.appliquerFiltres();
  }

  appliquerFiltres(): void {
    this.dataSource.filter = JSON.stringify({
      recherche: this.recherche.trim().toLowerCase(),
      organisations: this.filtreOrganisation,
      parcours: this.filtreParcours,
      etiquettes: this.filtreEtiquette,
      statuts: this.filtreStatut,
      actifUtilisateur: this.filtreActifUtilisateur
    });
    this.dataSource.paginator?.firstPage();
    this.recalculerStats();
  }

  private recalculerStats(): void {
    const lignes = this.dataSource.filteredData;

    this.stats = {
      utilisateursUniques: new Set(lignes.map(ligne => ligne.utilisateurId)).size,
      assignationsTotales: lignes.length,
      completes: lignes.filter(ligne => ligne.statut === EStatutRapportParcours.Complete).length,
      enCours: lignes.filter(ligne => ligne.statut === EStatutRapportParcours.EnCours).length,
      aRefaire: lignes.filter(ligne => ligne.statut === EStatutRapportParcours.ARefaire).length,
      nonDemarres: lignes.filter(ligne => ligne.statut === EStatutRapportParcours.NonDemarre).length
    };
  }

  reinitialiserFiltres(): void {
    this.recherche = '';
    this.filtreOrganisation = [];
    this.filtreParcours = [];
    this.filtreEtiquette = [];
    this.filtreStatut = [];
    this.filtreActifUtilisateur = 'actif';
    if (this.filtreParcoursFormationId) {
      this.reinitialiserNavigation.emit();
      return;
    }
    this.appliquerFiltres();
  }

  exporterExcel(): void {
    this.isLoading = true;
    const ligneIds = this.obtenirLigneIdsFiltres();
    this.rapportService.exporterUtilisateurs(this.filtreParcoursFormationId, ligneIds)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(blob => this.telecharger(blob, 'rapport_utilisateurs.xlsx'));
  }

  exporterPdf(): void {
    this.isLoading = true;
    const ligneIds = this.obtenirLigneIdsFiltres();
    this.rapportService.exporterUtilisateursPdf(this.filtreParcoursFormationId, ligneIds)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(blob => this.telecharger(blob, 'rapport_utilisateurs.pdf'));
  }

  private obtenirLigneIdsFiltres(): { parcoursFormationId: number; utilisateurId: number }[] {
    const toutesLesLignes = this.dataSource.data.length;
    const lignesFiltrees = this.dataSource.filteredData.length;
    if (lignesFiltrees === toutesLesLignes) return [];
    return this.dataSource.filteredData.map(l => ({ parcoursFormationId: l.parcoursFormationId, utilisateurId: l.utilisateurId }));
  }

  classeStatut(statut: EStatutRapportParcours): string {
    switch (statut) {
      case EStatutRapportParcours.Complete:   return 'statut-badge statut-complete';
      case EStatutRapportParcours.EnCours:    return 'statut-badge statut-encours';
      case EStatutRapportParcours.ARefaire:   return 'statut-badge statut-arefaire';
      default:                                return 'statut-badge statut-nondemarre';
    }
  }

  classeProgression(statut: EStatutRapportParcours): string {
    return statut === EStatutRapportParcours.Complete ? 'progress-green' : 'progress-blue';
  }

  private telecharger(blob: Blob, nomFichier: string): void {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = nomFichier;
    link.click();
    window.URL.revokeObjectURL(link.href);
  }

  private obtenirOptions(valeurs: string[]): string[] {
    return [...new Set(valeurs.filter(valeur => !!valeur))].sort((a, b) => a.localeCompare(b));
  }

  private synchroniserFiltreNavigation(lignes: RapportUtilisateurParcoursLigneModel[]): void {
    if (!this.filtreParcoursFormationId) return;

    const ligne = lignes.find(l => l.parcoursFormationId === this.filtreParcoursFormationId);
    this.filtreParcours = ligne?.nomParcours ? [ligne.nomParcours] : [];
  }
}
